import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';

import download from 'downloadjs';
import { toBlob } from 'html-to-image';

import { TranslatePipe } from '@core/i18n-legacy';
import { ReportChartComponent } from '@core/reports-ui';
import type { ChatPresenterComponent } from '@core/tellent-assistant-contracts';
import { normalizeChartOptions } from '@core/tellent-assistant-util';
import {
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

import type { PresentChartArgs } from './present-chart.types';

const CHART_HEIGHT_PX = 280;
const EXPORT_PIXEL_RATIO = 2;

@Component({
  selector: 'rt-assistant-present-chart',
  imports: [
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    ReportChartComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #card class="card rt-card" [class.menu-open]="menuOpen()">
      <rt-icon-button
        #menuOrigin="rtOverlayOrigin"
        rt-overlay-origin
        class="menu"
        variant="plain"
        iconName="more-horiz"
        size="small"
        [active]="menuOpen()"
        (click)="menuOpen.set(true)"
      />
      @if (title(); as titleText) {
        <rt-heading class="title" variant="heading semi-bold">{{ titleText }}</rt-heading>
      }
      <div class="chart" [style.height.px]="chartHeightPx">
        <rt-report-chart [options]="options()" />
      </div>

      <ng-template
        rt-connected-overlay
        [origin]="menuOrigin"
        [open]="menuOpen()"
        [positions]="menuPositions"
        [offsetY]="4"
        [width]="180"
        hasBackdrop
        withOverlayLevel
        (backdropClick)="menuOpen.set(false)"
      >
        <rt-dropdown>
          <rt-dropdown-item (click)="menuOpen.set(false); exportImage()">
            <rt-icon name="file-png" prefix />{{
              'tlc.tellent_assistant.chart.export_image' | translate
            }}
          </rt-dropdown-item>
        </rt-dropdown>
      </ng-template>
    </div>
  `,
  styleUrl: './present-chart.component.less',
})
export class AssistantPresentChartComponent implements ChatPresenterComponent<PresentChartArgs> {
  private readonly card = viewChild.required<ElementRef<HTMLDivElement>>('card');
  private readonly fileName = computed(() => {
    const slug = (this.title() ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return slug || 'chart';
  });

  protected readonly chartHeightPx = CHART_HEIGHT_PX;
  protected readonly title = computed(() => this.args().title);
  protected readonly options = computed(() => normalizeChartOptions(this.args().options));
  protected readonly menuOpen = signal<boolean>(false);
  protected readonly menuPositions = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];

  public readonly args = input.required<PresentChartArgs>();

  protected async exportImage(): Promise<void> {
    const blob = await toBlob(this.card().nativeElement, {
      pixelRatio: EXPORT_PIXEL_RATIO,
      filter: node => !(node instanceof HTMLElement && node.classList.contains('menu')),
    });
    if (!blob) return;
    download(blob, `${this.fileName()}.png`, 'image/png');
  }
}
